import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { StatusVariant } from '@/utils/statusGrammar';



export interface ErpBadgeProps extends React.ComponentProps<"span"> {
  variant?: StatusVariant | string;
  pill?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const ErpBadge = React.forwardRef<HTMLSpanElement, ErpBadgeProps>(({
  variant = 'neutral',
  pill,
  children,
  className = '',
  ...props
}, ref) => {
  let mappedVariant: "default" | "secondary" | "destructive" | "outline" = "default";
  let extraClasses = "";

  const v = String(variant).toLowerCase();

  // `outline-*` first: substring checks like `v.includes('danger')` would
  // otherwise swallow 'outline-danger' and render it as destructive.
  if (v.includes('outline')) {
    mappedVariant = "outline";
  } else if (v.includes('danger') || v.includes('destructive') || v.includes('error') || v.includes('failed') || v === 'unpaid') {
    mappedVariant = "destructive";
  } else if (v.includes('secondary') || v.includes('light') || v.includes('neutral')) {
    mappedVariant = "secondary";
  }
  
  // Custom colors for specific ERP statuses
  if (v === 'success' || v === 'completed' || v === 'paid' || v === 'received') {
    mappedVariant = "default";
    extraClasses = "bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:text-emerald-300";
  } else if (v.includes('outline-success')) {
    mappedVariant = "outline";
    extraClasses = "bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:text-emerald-300";
  } else if (v === 'warning' || v === 'ordered' || v === 'partial' || v === 'partially_paid' || v === 'pending') {
    mappedVariant = "default";
    extraClasses = "bg-amber-500/10 text-amber-800 border-amber-500/25 dark:text-amber-300";
  } else if (v.includes('outline-warning')) {
    mappedVariant = "outline";
    extraClasses = "bg-amber-500/10 text-amber-800 border-amber-500/25 dark:text-amber-300";
  } else if (v === 'info') {
    mappedVariant = "default";
    extraClasses = "bg-sky-500/10 text-sky-700 border-sky-500/25 dark:text-sky-300";
  } else if (v.includes('outline-info')) {
    mappedVariant = "outline";
    extraClasses = "bg-sky-500/10 text-sky-700 border-sky-500/25 dark:text-sky-300";
  } else if (v.includes('outline-danger')) {
    mappedVariant = "outline";
    extraClasses = "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800";
  }

  const classNames = [
    pill ? 'rounded-full px-2.5 py-0.5' : '',
    extraClasses,
    className
  ].filter(Boolean).join(' ');

  return (
    <Badge variant={mappedVariant} className={classNames} ref={ref} {...props}>
      {children}
    </Badge>
  );
});

export default React.memo(ErpBadge);
